import React from 'react';
import { Alert } from '@mui/material';
import { SnackableCourseData, DeploymentEnvironment } from '../../../types/moodleTypes';
import { StagedCoursePreview } from './StagedCoursePreview';
import { DeployedCoursePreview } from './DeployedCoursePreview';

interface Props {
  course: SnackableCourseData | null;
  deploymentState: DeploymentEnvironment;
  onDeploymentPreviewError?: (ctx: {
    state: 'staging' | 'production';
    moodleCourseId?: number;
    error: Error;
  }) => void;
}

const CoursePreview: React.FC<Props> = ({ course, deploymentState, onDeploymentPreviewError }) => {
  if (!course) {
    return <Alert severity="warning">No course content available to preview</Alert>;
  }

  const c: any = course as any;

  const hasDraftContent = Boolean(c?.staged_course);
  const hasStagingMoodleId = Boolean(c?.staged_moodle_id);
  const hasProdMoodleId = Boolean(c?.deployed_moodle_id);

  if (deploymentState === 'production') {
    const productionDeployment =
      (course as any).production_deployment ||
      (hasProdMoodleId
        ? {
          course_id: Number(c.deployed_moodle_id),
          moodle_url: 'https://moodle.cisco.com',
          deployed_at: c?.last_updated || new Date().toISOString(),
          status: 'active' as const,
        }
        : null);

    if (!productionDeployment) {
      return (
        <Alert severity="error">
          Course has not been deployed to production yet. Deploy from staging to preview production content.
        </Alert>
      );
    }

    return (
      <DeployedCoursePreview
        moodleCourseId={productionDeployment.course_id}
        moodleUrl={productionDeployment.moodle_url}
        deploymentState="production"
        deploymentInfo={productionDeployment as any}
        onLoadError={(error) =>
          onDeploymentPreviewError?.({
            state: 'production',
            moodleCourseId: productionDeployment.course_id,
            error,
          })
        }
      />
    );
  }

  if (deploymentState === 'staging') {
    const stagingDeployment =
      (course as any).staging_deployment ||
      (hasStagingMoodleId
        ? {
          course_id: Number(c.staged_moodle_id),
          moodle_url: 'https://staging-moodle.cisco.com',
          deployed_at: c?.last_updated || new Date().toISOString(),
          status: 'active' as const,
        }
        : null);

    if (stagingDeployment) {
      return (
        <DeployedCoursePreview
          moodleCourseId={stagingDeployment.course_id}
          moodleUrl={stagingDeployment.moodle_url}
          deploymentState="staging"
          deploymentInfo={stagingDeployment as any}
          onLoadError={(error) =>
            onDeploymentPreviewError?.({
              state: 'staging',
              moodleCourseId: stagingDeployment.course_id,
              error,
            })
          }
        />
      );
    }

    if (hasDraftContent) {
      return <StagedCoursePreview course={c.staged_course} deploymentState="draft" />;
    }

    return (
      <Alert severity="warning">
        Course has not been deployed to staging yet. Deploy to preview live content.
      </Alert>
    );
  }

  if (deploymentState === 'draft') {
    const draftData = c?.staged_course;

    if (!draftData || (!draftData.sections && !draftData.pages)) {
      return (
        <Alert severity="info">
          No draft content available. Create course structure to preview.
        </Alert>
      );
    }

    return <StagedCoursePreview course={draftData} deploymentState="draft" />;
  }

  return <Alert severity="error">Invalid deployment state: {deploymentState}</Alert>;
};

export default CoursePreview;